import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { patchApplicationTargetStatus } from 'api/applications'
import { markApplicationSeenIfNew } from '../utils/markApplicationSeen'
import { useApplicationsPolling } from './useApplicationsPolling'
import { applicationsStore } from '../model/applicationsStore'
import { applicationsFiltersStore } from '../model/applicationsFiltersStore'
import { ReactComponent as FilterIcon } from 'assets/icons/filter_empty.svg'
import { useFiltersSortBarApplications } from './useFiltersSortBarApplications'
import { ROUTES } from 'shared/config/routes'
import {
	COUNSELOR_STATUS_OPTIONS,
	normalizeApplicationStatusKey,
} from 'pages/ApplicationDetailsPage/utils/applicationStatusUi'
import { useSafeErrorBanner } from 'shared/components/ErrorBanner/useSafeErrorBanner'
import StatusProgressIcon from 'pages/ApplicationDetailsPage/components/StatusProgressIcon/StatusProgressIcon'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'

export const useApplicationsPage = () => {
	const navigate = useNavigate()
	const filtersSortBar = useFiltersSortBarApplications()
	const { showErrorSafe } = useSafeErrorBanner()

	useApplicationsPolling(true)

	useEffect(() => {
		ensureStudentsLoaded().catch(() => {})
	}, [])

	const rows = applicationsStore.filteredApplications
	const sortKey = applicationsStore.sortKey
	const sortDir = applicationsStore.sortDir

	const applications = applicationsStore.applications

	const applicationsCount = applications.length

	const studentsCount = useMemo(() => {
		const ids = new Set(
			applications
				.map(a => String(a?.studentId || a?.studentName || '').trim())
				.filter(Boolean)
		)
		return ids.size
	}, [applications])

	const tabs = (() => {
		const all = applications.length
		const inReview = applications.filter(x => x.result === 'inReview').length
		const draft = applications.filter(x => x.result === 'draft').length

		return [
			{ id: 'all', label: 'All Applications', count: all },
			{ id: 'inReview', label: 'In Review', count: inReview },
			{ id: 'draft', label: 'Draft', count: draft, isDifferent: true },
		]
	})()

	const actions = useMemo(() => {
		return [
			{
				id: 'filter',
				icon: <FilterIcon />,
				onClick: () => applicationsFiltersStore.toggle(),
			},
			// {
			// 	id: 'settings',
			// 	icon: <SettingsIcon />,
			// 	typeIconHover: 'stroke',
			// 	onClick: () => {},
			// },
		]
	}, [])

	const bulkUpdateApplicationsStatus = useCallback(
		async nextStatus => {
			const normalizedStatus = normalizeApplicationStatusKey(nextStatus)
			if (!normalizedStatus) return
			const selectedIds = applicationsStore.selectedIds || []
			if (!selectedIds.length) return

			const targets = selectedIds
				.map(id => {
					const row = applicationsStore.applications.find(
						item => String(item?.id || '').trim() === String(id || '').trim()
					)
					const targetId = String(row?.applicationTargetId || row?.id || '').trim()
					return targetId
						? {
								rowId: String(row?.id || '').trim(),
								targetId,
						  }
						: null
				})
				.filter(Boolean)

			if (!targets.length) return

			const results = await Promise.allSettled(
				targets.map(({ targetId }) =>
					patchApplicationTargetStatus(targetId, normalizedStatus)
				)
			)

			const successRowIds = targets
				.filter((_, i) => results[i]?.status === 'fulfilled')
				.map(x => x.rowId)
			const failedCount = results.length - successRowIds.length

			successRowIds.forEach(id => {
				applicationsStore.patchApplication(id, {
					applicationStatus: normalizedStatus,
				})
			})

			if (failedCount > 0) {
				showErrorSafe(
					`Could not update ${failedCount} application${failedCount > 1 ? 's' : ''}.`
				)
			}
		},
		[showErrorSafe]
	)

	const actionsSelection = useMemo(() => {
		return [
			{
				id: 'update',
				label: 'Update Status',
				onClick: () => {},
				icon: (
					<StatusProgressIcon
						status="waitlisted"
						arcColor="#ffffff"
						trackColor="rgba(255, 255, 255, 0.38)"
						ringColor="rgba(255, 255, 255, 0.85)"
						size={14}
					/>
				),
			},
			{
				id: 'assign',
				label: 'Assign Essay',
				onClick: () => {},
				iconName: 'dock',
			},
			{
				id: 'deadline',
				label: 'Set Deadline',
				onClick: () => {},
				iconName: 'clock',
			},
		]
	}, [])

	const onSortChange = useCallback(opt => {
		if (!opt?.key || !opt?.dir) return
		applicationsStore.setSort(opt.key, opt.dir)
	}, [])

	const clearSortState = useCallback(() => {
		applicationsStore.clearSort()
	}, [])

	const navigateToApplicationDetails = useCallback(
		row => {
			const id = row?.id
			if (id == null || id === '') return
			if (row?.isNewApplication) {
				void markApplicationSeenIfNew(row)
			}
			const encoded = encodeURIComponent(String(id))
			navigate(`${ROUTES.APPLICATIONS}/${encoded}`, {
				state: { backTo: { type: 'applications' } },
			})
		},
		[navigate],
	)

	const isFiltersSortVisible =
		filtersSortBar.isVisible ||
		filtersSortBar.baseItems.length > 0 ||
		filtersSortBar.extraItems.length > 0

	return {
		tabs,
		actions,
		actionsSelection,
		activeTab: applicationsStore.activeTab,
		setActiveTab: applicationsStore.setTab,

		rows,
		sortKey,
		sortDir,
		onSortChange,

		// Panel filters (FiltersSortBar)
		baseItems: filtersSortBar.baseItems,
		activeIds: filtersSortBar.activeIds,
		onBaseChange: filtersSortBar.onBaseChange,
		extraItems: filtersSortBar.extraItems,
		setExtraItems: filtersSortBar.setExtraItems,
		clearSort: filtersSortBar.onRemoveExtra,

		hasSelection: applicationsStore.hasSelection,
		selectedCount: applicationsStore.selectedCount,
		selectedIds: applicationsStore.selectedIds,
		toggleOne: applicationsStore.toggleOne,
		toggleAll: applicationsStore.toggleAll,
		clearSelection: applicationsStore.clearSelection,

		isFiltersSortVisible,
		isMergeByStudent: applicationsStore.isMergeByStudent,
		toggleMergeByStudent: applicationsStore.toggleMergeByStudent,

		isOpenFilter: applicationsFiltersStore.isOpen,
		openFilter: applicationsFiltersStore.open,
		closeFilter: applicationsFiltersStore.close,
		activeColumns: applicationsFiltersStore.activeColumns,
		counselorStatusOptions: COUNSELOR_STATUS_OPTIONS,
		bulkUpdateApplicationsStatus,

		navigateToApplicationDetails,

		applicationsCount,
		studentsCount,
		removeOnePanelFilter: filtersSortBar.removeOnePanelFilter,
		clearAllPanelFilters: filtersSortBar.clearAllPanelFilters,
		clearSortState,
	}
}
